import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import * as moment from 'moment';
import { setFormTouched } from 'src/app/utils/form';
import { PACKAGE_SIZE, ROUTE_TYPE, PACKAGE_TYPE } from 'src/app/constants';
import { AlertService } from 'src/app/services/alert/alert.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { RouteService } from 'src/app/services/routes/route.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Events } from '@ionic/angular';
import { FindRouteParams } from 'src/app/models/findRouteParams';
import { Route } from 'src/app/models/route';
import { Storage } from '@ionic/storage';
import { AlertController, ModalController } from '@ionic/angular';
import { SendObjectsPage } from '../send/send-objects/send-objects.page';
import { from } from 'rxjs';

const INPUT_FORMAT = 'DD/MM/YYYY';
const MAX_PACKAGE_WEIGHT = 75 * 10 * 10 * 10;
const MAX_PACKAGE_NUMBERS = 50 * 10 * 10 * 10;

@Component({
  selector: 'app-send-upsert',
  templateUrl: './send-upsert.page.html',
  styleUrls: ['./send-upsert.page.scss'],
})
export class SendUpsertPage implements OnInit {
  form: FormGroup;
  packageSizes = Object.keys(PACKAGE_SIZE);
  packageTypes = Object.keys(PACKAGE_TYPE);
  minDate = new Date();
  date = moment();

  verboseFromProvince: string;
  verboseFromRegion: string;
  verboseToProvince: string;
  verboseToRegion: string;

  sendObjects = [];

  constructor(
    protected fb: FormBuilder,
    private alert: AlertService,
    private spinner: SpinnerService,
    private routeService: RouteService,
    private auth: AuthService,
    private location: Location,
    private events: Events,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private storage: Storage,
    private modalController: ModalController,
    private alertController: AlertController,
  ) {
    this.form = this.fb.group({
      type: [ROUTE_TYPE.send, Validators.required],
      from: ['', [Validators.required]],
      to: ['', Validators.required],
      date: ['', Validators.required],
      arrivalDate: ['', Validators.required],
      packageSize: [''],
      packageType: [],
      packagesNumber: [, [Validators.min(0), Validators.max(MAX_PACKAGE_NUMBERS)]],
      packageWeight: [, [Validators.required, Validators.min(1), Validators.max(MAX_PACKAGE_WEIGHT)]],
      packageFragile: [false],
      packageFlammable: [false],
      gpsEnabled: [false],
      professionalCarrier: [false],
      fromCountryCode: [''],
      toCountryCode: [''],
    });
  }

  get sendObjectsArray(): any[] {
    console.log('sendObjectsArray getter called, value:', this.sendObjects);
    return Array.isArray(this.sendObjects) ? this.sendObjects : [];
  }

  // Per aggiungere oggetti (comportamento attuale)
    async openSendObjects() {
      const currentObjects = this.form.get('sendObjects') ? this.form.get('sendObjects').value : [];
      
      const modal = await this.modalController.create({
        component: SendObjectsPage,
        componentProps: {
          objects: currentObjects
        }
      });
  
      modal.onDidDismiss().then((result) => {
        if (result.data && result.data.objects) {
          this.sendObjects.push(...result.data.objects);
          
          const totalWeight = result.data.objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
          this.form.patchValue({
            packageWeight: totalWeight
          });
        }
      });
  
      return await modal.present();
    }
  
    // Per modificare un oggetto specifico
    async editSendObject(index: number) {
      const currentObjects = this.sendObjects || [];
      const objectToEdit = currentObjects[index];
      
      const modal = await this.modalController.create({
        component: SendObjectsPage,
        componentProps: {
          objects: currentObjects,
          objectToEdit: objectToEdit,
          editIndex: index
        }
      });
  
      modal.onDidDismiss().then((result) => {
        
        if (result.data && result.data.objects) {
          const updatedObjects = result.data.objects;
          const updatedObject = updatedObjects[index];
          if (updatedObject) {
            this.sendObjects[index] = updatedObject;
          }
          
          const totalWeight = result.data.objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
          this.form.patchValue({
            packageWeight: totalWeight
          });
        }
      });
  
      return await modal.present();
    }

  getSendObjectsCount() {
    console.log('sendObjects', this.sendObjects);
    return this.sendObjects ? this.sendObjects.length : 0;
  }
  
  getId() {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    return id;
  }

  async ngOnInit() {
    // Carica i sendObjects dallo storage se esistono
    await this.loadSendObjectsFromStorage();

    const id = this.getId();
    if (!id) {
      this.activatedRoute.queryParams.subscribe((params: FindRouteParams) => {
        const value = { ...params };
        this.verboseFromProvince = value.verboseFromProvince ? value.verboseFromProvince : '';
        this.verboseFromRegion = value.verboseFromRegion ? value.verboseFromRegion : '';
        this.verboseToProvince = value.verboseToProvince ? value.verboseToProvince : '';
        this.verboseToRegion = value.verboseToRegion ? value.verboseToRegion : '';

        delete value.type;
        this.initForm(value);
        this.selectedDate(moment(value.date));
        if (value.arrivalDate) {
          this.selectedArrivalDate(moment(value.arrivalDate));
        }
      });
      return;
    }

    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      const route = await this.routeService.get(token, id);

      route.date = moment(route.date).format(INPUT_FORMAT);
      if (route.arrivalDate) {
        route.arrivalDate = moment(route.arrivalDate).format(INPUT_FORMAT);
      }
      this.initForm(route);

      this.sendObjects = route.routeObjects || [];

      await this.spinner.dismiss();
    } catch (err) {
      await this.spinner.dismissWithAlertError(err);
    }
  }

  selectedFrom(place) {
    const { description = '', province = '', region = '' } = place || {};
    this.form.get('from').setValue(description);

    this.verboseFromProvince = province;
    this.verboseFromRegion = region;
  }

  selectedTo(place) {
    const { description = '', province = '', region = '' } = place || {};
    this.form.get('to').setValue(description);

    this.verboseToProvince = province;
    this.verboseToRegion = region;
  }

  hasSelectedFrom() {
    return !!this.form.get('from').value;
  }

  hasSelectedTo() {
    return !!this.form.get('to').value;
  }

  selectedDate(date: moment.Moment) {
    this.date = date;
    this.form.get(`date`).setValue(date.format(INPUT_FORMAT));
    const arrivalDateValue = this.form.get(`arrivalDate`).value;

    // If arrival is before trip date, set it null
    if (arrivalDateValue) {
      if (moment(arrivalDateValue, INPUT_FORMAT).isBefore(date, 'day')) {
        this.form.get(`arrivalDate`).setValue(null);
      }
    }
  }

  selectedArrivalDate(date: moment.Moment) {
    this.form.get(`arrivalDate`).setValue(date.format(INPUT_FORMAT));
  }

  async onSubmit() {
    const totalWeight = this.sendObjects.reduce((sum, obj) => {
      const weight = parseFloat(obj.weight) || 0;
      return sum + weight;
    }, 0);
        this.form.patchValue({
          packageWeight: Number(totalWeight.toFixed(2))
        });

    if (!this.form.valid) {
      setFormTouched(this.form);

      // Controlla se sendObjects è vuoto e mostra un errore
      if (!this.sendObjects || this.sendObjects.length === 0) {
        this.alert.toastWarning('WARNING_NO_OBJECTS_ADDED');
        return;
      }

      this.alert.toastWarning('WARNING_FORM_INVALID');
      return;
    }

    const route = this.getFormValue();

    try {
      await this.spinner.show();
      const token = await this.auth.getToken();

      if (this.verboseFromProvince) {
        route.verboseFromProvince = this.verboseFromProvince;
      }
      if (this.verboseFromRegion) {
        route.verboseFromRegion = this.verboseFromRegion;
      }
      if (this.verboseToProvince) {
        route.verboseToProvince = this.verboseToProvince;
      }
      if (this.verboseToRegion) {
        route.verboseToRegion = this.verboseToRegion;
      }

      // Aggiungi sendObjects come routeObjects al JSON
      route.routeObjects = this.sendObjects.map(obj => ({
        name: obj.name,
        width: obj.width,
        height: obj.height,
        length: obj.length,
        weight: obj.weight
      }));

      const id = this.getId();
      if (id) {
        await this.routeService.update(token, id, route);
      } else {
        await this.routeService.create(token, route);
      }
      
      await this.spinner.dismiss();
      await this.alert.toastSuccess();

      // Cancella i sendObjects dallo storage dopo il successo della chiamata
      // await this.clearSendObjectsFromStorage();

      this.events.publish('send:upsert', { id });

      if (!id) {
        this.router.navigate(['/tabs/my-sends'], { replaceUrl: true });
      } else {
        this.location.back();
      }
    } catch (err) {
      this.spinner.dismissWithAlertError(err);
    }
  }

  getFormValue() {
    const route = this.form.value;
    const { packageSize, date, arrivalDate, packageType } = route;

    route.date = moment(date, INPUT_FORMAT);

    if (arrivalDate) {
      route.arrivalDate = moment(arrivalDate, INPUT_FORMAT);
    } else {
      delete route.arrivalDate;
    }

    if (typeof packageSize !== 'string') {
      delete route.packageSize;
    }
    if (typeof packageType !== 'string') {
      delete route.packageType;
    }

    return route;
  }

  initForm(value: Route | FindRouteParams) {
    const { maximumPrice, packageType } = value;
    
    if (!packageType) {
      value.packageType = PACKAGE_TYPE.other;
    }

    if (this.getId() && !maximumPrice) {
      delete value.maximumPrice;
    }
    
    this.form.patchValue(value);
  }

  private async loadSendObjectsFromStorage(): Promise<void> {
    try {
      const savedObjects = await this.storage.get('send_objects');
      console.log('SendObjects trovati nello storage:', savedObjects);
      if (savedObjects && Array.isArray(savedObjects) && savedObjects.length > 0) {
        this.sendObjects = savedObjects;
        console.log('SendObjects caricati dallo storage:', this.sendObjects);
      } else {
        console.log('Nessun SendObject trovato nello storage.');
        this.sendObjects = [];
      }
    } catch (error) {
      console.error('Errore nel caricare sendObjects dallo storage:', error);
    }
  }

  private async clearSendObjectsFromStorage(): Promise<void> {
    try {
      await this.storage.remove('send_objects');
      console.log('SendObjects rimossi dallo storage dopo submit con successo');
    } catch (error) {
      console.error('Errore nel rimuovere sendObjects dallo storage:', error);
    }
  }

  async confirmRemoveObject(index: number) {
    const objectName = this.sendObjects[index].name || 'questo oggetto';
    
    const alert = await this.alertController.create({
      header: 'Conferma rimozione',
      message: `Sei sicuro di voler rimuovere "${objectName}"?`,
      buttons: [
        {
          text: 'Annulla',
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: 'Rimuovi',
          cssClass: 'danger',
          handler: () => {
            this.removeObject(index);
          }
        }
      ]
    });

    await alert.present();
  }

  async removeObject(index: number) {
    const objects = this.sendObjects;
    if (objects && objects.length > index) {
      objects.splice(index, 1);
      this.form.patchValue({ sendObjects: objects });

      const totalWeight = objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
      this.form.patchValue({
        packageWeight: totalWeight
      });
    }
  }
}
